/**
 * Хук экстренной заявки SOS (P0, design-guest §15.4 — отдельная красная
 * кнопка). Создаёт срочную заявку: POST /v1/requests с приоритетом high
 * (бэк понижает guest-supplied urgent → high; AllowUrgent=false для гостя —
 * это инвариант домена, не баг), категория — приёмная/ресепшен (front_desk),
 * чтобы заявка ушла в очередь персоналу немедленно.
 *
 * Категорию резолвим из витрины каталога тенанта (getServiceCatalog): берём
 * front_desk / reception; если их нет — maintenance; иначе первую активную.
 * Так SOS работает на любом тенанте без хардкода UUID.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createServiceRequest, getServiceCatalog } from '@hsc/api-client';
import type {
  CatalogViewCategory,
  CatalogViewResponse,
  ServiceCategoryId,
  ServiceRequestCreatedResponse,
} from '@hsc/types';

/** Предпочтительные коды категорий для экстренной заявки (по убыванию). */
const SOS_CATEGORY_PRIORITY = ['front_desk', 'reception', 'concierge', 'maintenance'];

/** Выбор категории для SOS из витрины каталога. */
export function pickSosCategory(
  categories: readonly CatalogViewCategory[],
): CatalogViewCategory | undefined {
  for (const code of SOS_CATEGORY_PRIORITY) {
    const hit = categories.find((c) => c.code === code);
    if (hit) return hit;
  }
  return categories[0];
}

export function useSosCatalog() {
  return useQuery<CatalogViewResponse>({
    queryKey: ['service-catalog', 'ru'],
    queryFn: () => getServiceCatalog('ru'),
  });
}

export interface SosInput {
  /** id категории заявки (резолвится из каталога). */
  categoryId: ServiceCategoryId;
  /** Свободное описание ситуации (необязательно). */
  description?: string;
  /** Привязка к проживанию/номеру, если известна. */
  stayId?: string;
}

export function useCreateSos() {
  const qc = useQueryClient();
  return useMutation<ServiceRequestCreatedResponse, Error, SosInput>({
    mutationFn: ({ categoryId, description, stayId }) =>
      createServiceRequest({
        category_id: categoryId,
        title: 'SOS — срочная помощь',
        priority: 'high',
        ...(description && description.trim() !== '' ? { description: description.trim() } : {}),
        ...(stayId ? { stay_id: stayId } : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['requests'] });
      void qc.invalidateQueries({ queryKey: ['me', 'requests'] });
    },
  });
}
